/**
 * @desc        Events API Redux
 * @version     1.0 (initial version) - 10 Nov 2025
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Axios from 'axios'
import { FARMDECK_API, TIME_LIMIT_FOR_FETCH } from 'app-config/index.js'
import { accountId } from 'helpers/account.js'
import fastCompare from 'react-fast-compare'
import { subMonths, getUnixTime } from 'date-fns'
import { reverseFormatAnimalIds, extractIndividualAnimals } from 'features-v2/animal-management/components/helpers/animal-ids-utils.js'
import { normalizeLegacyChemicalEvents, rowMatchesLegacyAsset, rowMatchesLegacyPaddocks } from 'helper-functions/events/asset-register/normalize-legacy-chemical-event.js'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'

// --- helpers ---
const baseUrl = tenant => FARMDECK_API.replace(':id', tenant ?? accountId).replace('/v1/', '/v2/events')

const shouldRefetch = (lastFetch, force, cache) => {
    if (force) return true
    if (!cache) return true
    const age = Date.now() - (lastFetch || 0)
    const ttl = typeof TIME_LIMIT_FOR_FETCH === 'number' && TIME_LIMIT_FOR_FETCH > 0 ? TIME_LIMIT_FOR_FETCH : 5 * 60 * 1000
    return age >= ttl
}

// --------------------- Thunks (CRUD-style names) ---------------------

export const createEvent = createAsyncThunk('farmEvents/createEvent', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}`
        const res = await Axios.post(url, data)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createEvent failed')
    }
})

export const updateEvent = createAsyncThunk('farmEvents/updateEvent', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/${data.id}`
        const res = await Axios.put(url, data)
        return { ...res.data, id: data.id }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateEvent failed')
    }
})

export const fetchEventById = async ({ tenant, id } = {}) => {
    const res = await Axios.get(`${baseUrl(tenant)}/${encodeURIComponent(id)}`)
    return res.data
}

export const fetchEventsQuery = async ({ tenant, ids, query = {} } = {}) => {
    let url = `${baseUrl(tenant)}?`
    if (ids) {
        url += `animalIds=${encodeURIComponent(JSON.stringify(ids))}`
    }
    const validatedQuery = Object.keys(query).length ? validateEventsQuery(query) : {}
    const res = await Axios.get(url, { params: validatedQuery, timeout: 30000 })
    return res.data
}

export const deleteEvent = createAsyncThunk('farmEvents/deleteEvent', async ({ id, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}/${id}`
        const res = await Axios.delete(url)
        return { ...res.data, id }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteEvent failed')
    }
})

/**
 * The events API returns at most 100 records when `maxResults` is omitted, and exposes no
 * pagination cursor. For animal history that silently truncates: an 84-head cohort with four
 * weigh sessions is already 336 events, so the older sessions vanish and every derived figure
 * (interval ADG, matched-animal counts, cohort spread, underperformer ranking) is computed
 * from partial history without any indication that data is missing. Callers fetching a
 * cohort's history must pass a ceiling; this is the shared one.
 *
 * TODO: replace with real pagination once the API offers a cursor - a ceiling only moves the
 * truncation point, it does not remove it.
 */
export const ANIMAL_HISTORY_MAX_RESULTS = 5000

export const LEGACY_EVENT_SOURCES = Object.freeze([
    { key: 'agrichemical', path: 'agrichemical', defaultType: 'agriChemicalEvent' },
    { key: 'pestchemical', path: 'pestchemical', defaultType: 'pestChemicalEvent' },
    { key: 'vetchemical', path: 'vetchemical', defaultType: 'vetChemicalEvent' },
    { key: 'spraying', path: 'spraying', defaultType: 'sprayingEvent' },
])

const LEGACY_SOURCE_BY_KEY = Object.freeze(Object.fromEntries(LEGACY_EVENT_SOURCES.map(s => [s.key, s])))

/** Resolve which legacy endpoints to fetch; omit / empty → all. Unknown keys ignored. */
export function resolveLegacyEventSources(sources) {
    const all = LEGACY_EVENT_SOURCES.map(s => s.key)
    if (!sources || !Array.isArray(sources) || sources.length === 0) return all
    const allowed = new Set(all)
    const resolved = [...new Set(sources.filter(k => allowed.has(k)))]
    if (resolved.length < sources.filter(Boolean).length) {
        console.warn('resolveLegacyEventSources: ignored unknown source key(s)', sources.filter(k => k && !allowed.has(k)))
    }
    return resolved.length ? resolved : all
}

export function resolveLegacyPaddockIds(paddockIds) {
    if (!paddockIds || !Array.isArray(paddockIds) || paddockIds.length === 0) return null
    const nums = [...new Set(paddockIds.map(x => parseInt(String(x), 10)).filter(n => !Number.isNaN(n) && n >= 0))].sort((a, b) => a - b)
    return nums.length ? nums : null
}

const legacyBaseUrl = (tenant, farmId, path) => `${FARMDECK_API.replace(':id', tenant ?? accountId)}farms/${farmId}/${path}/`

const legacyCacheKeyPartsEqual = (a, b) => {
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    return String(a) === String(b)
}

const sameLegacyCache = (state, { farmId, assetId, sourcesKey, paddockIdsKey }) =>
    legacyCacheKeyPartsEqual(state.legacyFarmId, farmId) &&
    legacyCacheKeyPartsEqual(state.legacyAssetId, assetId == null || assetId === '' ? null : assetId) &&
    legacyCacheKeyPartsEqual(state.legacySourcesKey, sourcesKey) &&
    legacyCacheKeyPartsEqual(state.legacyPaddockIdsKey, paddockIdsKey)

/**
 * Fetch and amalgamate v1 agri/pest/vet/spraying records, optionally filter, normalise to modern envelopes.
 * @returns {Promise<object[]>}
 */
export async function fetchLegacyEventsQuery({ tenant, farmId, assetId, sources, paddockIds } = {}) {
    console.log('fetchLegacyEventsQuery', tenant, farmId, assetId, sources, paddockIds)
    if (farmId == null || farmId === '') {
        throw new Error('fetchLegacyEventsQuery: farmId is required')
    }
    const resolvedSources = resolveLegacyEventSources(sources)
    const resolvedPaddocks = resolveLegacyPaddockIds(paddockIds)
    const defs = resolvedSources.map(key => LEGACY_SOURCE_BY_KEY[key]).filter(Boolean)

    const settled = await Promise.allSettled(
        defs.map(async def => {
            const res = await Axios.get(legacyBaseUrl(tenant, farmId, def.path), { timeout: 30000 })
            const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []
            return items.map(row => ({
                ...row,
                type: row?.type || def.defaultType,
                __legacySource: def.key,
                __defaultLegacyType: def.defaultType,
            }))
        }),
    )

    const raw = []
    for (const result of settled) {
        if (result.status === 'fulfilled' && Array.isArray(result.value)) raw.push(...result.value)
    }

    const filtered = raw.filter(row => rowMatchesLegacyAsset(row, assetId) && rowMatchesLegacyPaddocks(row, resolvedPaddocks || []))

    const normalised = filtered.flatMap(row =>
        normalizeLegacyChemicalEvents([row], {
            farmId,
            legacySource: row.__legacySource,
            defaultLegacyType: row.__defaultLegacyType,
        }),
    )

    normalised.sort((a, b) => {
        const ta = a.time ? Date.parse(a.time) : 0
        const tb = b.time ? Date.parse(b.time) : 0
        return tb - ta
    })

    return normalised
}

export const fetchLegacyEvents = createAsyncThunk('farmEvents/fetchLegacyEvents', async ({ force = false, tenant, farmId, assetId, sources, paddockIds } = {}, { getState, rejectWithValue }) => {
    try {
        if (farmId == null || farmId === '') {
            return rejectWithValue('fetchLegacyEvents: farmId is required')
        }
        const resolvedSources = resolveLegacyEventSources(sources)
        const resolvedPaddocks = resolveLegacyPaddockIds(paddockIds)
        const sourcesKey = [...resolvedSources].sort().join(',')
        const paddockIdsKey = resolvedPaddocks ? resolvedPaddocks.join(',') : null
        const assetKey = assetId == null || assetId === '' ? null : assetId
        const state = getState().farmEvents
        if (!force && state.legacyLastFetch && !shouldRefetch(state.legacyLastFetch, false, true) && sameLegacyCache(state, { farmId, assetId: assetKey, sourcesKey, paddockIdsKey })) {
            return { cached: true, items: state.legacyData, farmId, assetId: assetKey, sources: resolvedSources, paddockIds: resolvedPaddocks, sourcesKey, paddockIdsKey }
        }
        const items = await fetchLegacyEventsQuery({ tenant, farmId, assetId, sources: resolvedSources, paddockIds: resolvedPaddocks || undefined })
        return { items, farmId, assetId: assetKey, sources: resolvedSources, paddockIds: resolvedPaddocks, sourcesKey, paddockIdsKey }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'fetchLegacyEvents failed')
    }
})

// ids = The ids of the animals/cohort/group to fetch events for (multi-range format)
// query = All optional query params for the events API
// query = { animalIds, farmIds, paddockIds, assetIds, userIds, types, from, to, order, maxResults }
// Multiple filtering parameters can be combined with AND logic
export const fetchEvents = createAsyncThunk('farmEvents/fetchEvents', async ({ force = false, cache = false, tenant, ids, query = {} } = {}, { getState, rejectWithValue }) => {
    const { lastFetch, data } = getState().farmEvents
    if (!data || shouldRefetch(lastFetch, force, cache)) {
        try {
            let url = `${baseUrl(tenant)}?`
            if (ids) {
                url += `animalIds=${encodeURIComponent(JSON.stringify(ids))}`
            }

            const validatedQuery = Object.keys(query).length ? validateEventsQuery(query) : {}
            const res = await Axios.get(url, { params: validatedQuery, timeout: 30000 })
            // if query contains fields, then strip out the fields from the response
            // didnt work as expected as delays in the response
            // if (query?.fields && res) {
            //     res.data = res.data.map(item => {
            //         console.log('item', item)
            //         return Object.fromEntries(Object.entries(item).filter(([key]) => !query.fields.includes(key)))
            //     })
            // }
            return res.data
        } catch (err) {
            console.log('error', err)
            return rejectWithValue(err?.response?.data || err.message || 'fetchEvents failed')
        }
    }
    console.log('returning cached data', data)
    return { cached: true, ...(data || {}) }
})

const KNOWN_QUERY_KEYS = new Set(['farmIds', 'paddockIds', 'assetIds', 'userIds', 'eventIds', 'types', 'from', 'to', 'order', 'maxResults', 'distinctByID'])

const toCommaInts = v => {
    const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : []
    const nums = arr.map(x => parseInt(String(x).trim(), 10)).filter(n => !Number.isNaN(n) && n >= 0)
    return nums.length ? nums.join(',') : null
}

const toCommaStrings = v => {
    const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : []
    const strs = arr.map(x => String(x).trim()).filter(Boolean)
    return strs.length ? strs.join(',') : null
}

export const validateEventsQuery = (query = {}) => {
    if (!query || typeof query !== 'object') return {}
    const out = {}
    for (const [k, v] of Object.entries(query)) {
        if (!KNOWN_QUERY_KEYS.has(k) || v === undefined || v === null) continue
        switch (k) {
            case 'farmIds':
            case 'paddockIds':
            case 'assetIds':
            case 'userIds':
            case 'eventIds': {
                const s = toCommaInts(v)
                if (s) out[k] = s
                break
            }
            case 'types': {
                const s = toCommaStrings(v)
                if (s) out[k] = s
                break
            }
            case 'from':
            case 'to': {
                const n = parseInt(v, 10)
                if (!Number.isNaN(n) && n >= 0) out[k] = n
                else throw new Error(`Invalid ${k}: must be non-negative Unix timestamp`)
                break
            }
            case 'order': {
                const s = String(v).toLowerCase()
                if (s === 'asc' || s === 'desc') out[k] = s
                else throw new Error('Invalid order: must be "asc" or "desc"')
                break
            }
            case 'maxResults': {
                const n = parseInt(v, 10)
                if (!Number.isNaN(n) && n > 0) out[k] = n
                else throw new Error('Invalid maxResults: must be positive integer')
                break
            }
            case 'distinctByID': {
                const b = Boolean(v)
                if (typeof b === 'boolean') out[k] = b
                else throw new Error('Invalid distinctByID: must be a boolean')
                break
            }
            default:
                break
        }
    }
    return out
}

// const from = getUnixTime(subMonths(new Date(), 1))
// const to = getUnixTime(new Date())
// const fetchQuery = {
//     ...query,
//     from,
//     to,
// }

// --------------------- Slice ---------------------
const initialState = {
    lastFetch: 0,
    loading: false,
    data: [],
    error: null,
    lastError: 0,
    legacyData: [],
    legacyLoading: false,
    legacyError: null,
    legacyLastFetch: 0,
    legacyFarmId: null,
    legacyAssetId: null,
    legacySources: null,
    legacySourcesKey: null,
    legacyPaddockIds: null,
    legacyPaddockIdsKey: null,
}

const farmEventsSlice = createSlice({
    name: 'farmEvents',
    initialState,
    reducers: {},
    extraReducers: builder => {
        // createEvent
        builder.addCase(createEvent.pending, state => {
            state.loading = true
        })
        builder.addCase(createEvent.fulfilled, (state, action) => {
            state.loading = false
            state.data = [...state.data, normalizeOneEvent(action.payload)]
            state.lastFetch = Date.now()
            state.error = null
        })
        builder.addCase(createEvent.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message || 'createEvent error'
            state.lastError = Date.now()
        })

        // updateEvent
        builder.addCase(updateEvent.pending, state => {
            state.loading = true
        })
        builder.addCase(updateEvent.fulfilled, (state, action) => {
            state.loading = false
            state.data = [...state.data.filter(event => event.id !== action.payload.id), normalizeOneEvent(action.payload)]
            state.lastFetch = Date.now()
            state.error = null
        })
        builder.addCase(updateEvent.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message || 'updateEvent error'
            state.lastError = Date.now()
        })

        // deleteEvent
        builder.addCase(deleteEvent.pending, state => {
            state.loading = true
        })
        builder.addCase(deleteEvent.fulfilled, (state, action) => {
            state.loading = false
            state.data = state.data.filter(event => event.id !== action.payload.id)
            state.lastFetch = Date.now()
            state.error = null
        })
        builder.addCase(deleteEvent.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message || 'deleteEvent error'
            state.lastError = Date.now()
        })

        // fetchEvents
        builder.addCase(fetchEvents.pending, state => {
            state.loading = true
        })
        builder.addCase(fetchEvents.fulfilled, (state, action) => {
            state.loading = false
            const items = action.payload?.items
            if (Array.isArray(items)) {
                state.data = normalizeEvents(items)
                state.lastFetch = Date.now()
            } else if (!action.payload?.cached) {
                state.data = normalizeEvents(items)
                state.lastFetch = Date.now()
            }
            state.error = null
        })
        builder.addCase(fetchEvents.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload || action.error?.message || 'fetchEvents error'
            state.lastError = Date.now()
        })

        builder.addCase(fetchLegacyEvents.pending, state => {
            state.legacyLoading = true
        })
        builder.addCase(fetchLegacyEvents.fulfilled, (state, action) => {
            state.legacyLoading = false
            const payload = action.payload || {}
            if (!payload.cached) {
                state.legacyData = Array.isArray(payload.items) ? payload.items : []
                state.legacyLastFetch = Date.now()
                state.legacyFarmId = payload.farmId ?? null
                state.legacyAssetId = payload.assetId ?? null
                state.legacySources = payload.sources ?? null
                state.legacySourcesKey = payload.sourcesKey ?? null
                state.legacyPaddockIds = payload.paddockIds ?? null
                state.legacyPaddockIdsKey = payload.paddockIdsKey ?? null
            }
            state.legacyError = null
        })
        builder.addCase(fetchLegacyEvents.rejected, (state, action) => {
            state.legacyLoading = false
            state.legacyError = action.payload || action.error?.message || 'fetchLegacyEvents error'
        })
    },
})

export default farmEventsSlice.reducer

export const selectLegacyEvents = state => state.farmEvents.legacyData

const normalizeEvents = events => {
    if (!Array.isArray(events)) return []
    return events.map(normalizeOneEvent)
}

/** API/storage type for livestock weighing events (RFID upload uses this). */
export const ANIMALS_WEIGHING_EVENT_TYPE = 'animalsWeighing'

/** Normalized in-app type after fetchEvents (see normalizeOneEvent). */
export const WEIGHING_EVENT_TYPE = 'weighing'

export function isWeighingEvent(event) {
    if (!event || typeof event !== 'object') {
        return false
    }

    if (event.type === ANIMALS_WEIGHING_EVENT_TYPE || event.type === WEIGHING_EVENT_TYPE) {
        return true
    }

    if (event.rawType === ANIMALS_WEIGHING_EVENT_TYPE) {
        return true
    }

    const kind = event.detail?.kind
    return typeof kind === 'string' && kind.startsWith('weighing.')
}

export function getWeighingEventWeightKg(event) {
    if (!isWeighingEvent(event)) {
        return null
    }

    const detail = event?.detail || {}
    const detailData = detail?.data || {}
    const weigh = detail?.weigh || {}
    const measurements = Array.isArray(detail?.measurements) ? detail.measurements : []
    const rawWeight = detailData?.weightKg ?? weigh?.weightKg ?? measurements[0]?.weightKg
    const weight = typeof rawWeight === 'number' ? rawWeight : Number(rawWeight)

    return typeof weight === 'number' && !Number.isNaN(weight) ? weight : null
}

function normalizeOneEvent(event) {
    if (!event || typeof event !== 'object') {
        return event
    }

    const animalIds = reverseFormatAnimalIds(event?.animalIds)
    if (event?.type !== ANIMALS_WEIGHING_EVENT_TYPE) {
        return { ...event, aIds: animalIds }
    }

    const detail = event?.detail || {}
    const detailData = detail?.data || {}
    const weightKg = detailData?.weightKg ?? null
    const weightUnit = detailData?.weightUnit ?? null
    const measuredAt = event?.time ?? detailData?.measuredAt ?? null

    return {
        ...event,
        aIds: animalIds,
        rawType: event.type,
        type: WEIGHING_EVENT_TYPE,
        detail: {
            ...detail,
            measurements: [
                {
                    animalRefSource: detailData?.animalRefSource ?? null,
                    measuredAt,
                    source: detailData?.source ?? null,
                    weightKg,
                    weightUnit,
                },
            ],
            weigh: {
                animalRefSource: detailData?.animalRefSource ?? null,
                automation: detailData?.automation ?? null,
                sessionId: detailData?.sessionId ?? null,
                source: detailData?.source ?? null,
                topology: detailData?.topology ?? null,
                transport: detailData?.transport ?? null,
                weightKg,
                weightUnit,
            },
        },
    }
}

export const useAppSelector = useSelector
export const useEvents = () => useAppSelector(s => s.farmEvents.data ?? [])
export const useEventsSummary = () => {
    const events = useEvents()
    return useMemo(() => summariseEvents(events), [events])
}

export function summariseEvents(events) {
    const empty = {
        aIds: [],
        types: [],
        farmIds: [],
        paddockIds: [],
    }
    if (!Array.isArray(events)) return empty

    const typeCount = new Map()
    const aIdCount = new Map()
    const farmIdCount = new Map()
    const paddockIdCount = new Map()

    for (const event of events) {
        if (event.type != null) {
            addCount(typeCount, event.type)
        }
        const aIds = event.aIds
        if (Array.isArray(aIds)) for (const id of aIds) addCount(aIdCount, id)
        const farmIds = event.farmIds
        if (Array.isArray(farmIds)) for (const id of farmIds) addCount(farmIdCount, id)
        const paddockIds = event.paddockIds
        if (Array.isArray(paddockIds)) for (const id of paddockIds) addCount(paddockIdCount, id)
    }

    return {
        aIds: mapToCountArray(aIdCount),
        types: mapToCountArray(typeCount),
        farmIds: mapToCountArray(farmIdCount),
        paddockIds: mapToCountArray(paddockIdCount),
    }
}

const addCount = (map, v, n = 1) => {
    if (v != null) map.set(v, (map.get(v) ?? 0) + (Number(n) || 0))
}
const mapToCountArray = map => Array.from(map.entries(), ([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || (a.value < b.value ? -1 : a.value > b.value ? 1 : 0))
